// @flow
import invariant from 'invariant';
import sum from 'lodash/fp/sum';
import regexParser from 'regex-parser';

import { asText } from 'modules/file';
import { type LINE_END, DEFAULT_LINE_END } from 'modules/string';

export type ColumnCommonProperties = $ReadOnly<{|
  width: number,
|}>;

export type NumberColumn = $ReadOnly<{|
  type: 'Number',
  valueTransformers?: $ReadOnlyArray<(number) => number>,
  ...ColumnCommonProperties,
|}>;

export type StringColumn = $ReadOnly<{|
  type: 'String',
  valueTransformers?: $ReadOnlyArray<
    ((string) => string) | { match: string, replace: string },
  >,
  ...ColumnCommonProperties,
|}>;

export type Column = StringColumn | NumberColumn;
export type FileSpecification = $ReadOnly<{|
  header: $ReadOnlyArray<Column> | null,
  columns: $ReadOnlyArray<Column>,
  trailer: $ReadOnlyArray<Column> | null,
  encoding?: string,
  lineDelimiter?: LINE_END | null,
|}>;

export type StringCell = $ReadOnly<{|
  type: 'String',
  value: string,
|}>;

export type NumberCell = $ReadOnly<{|
  type: 'Number',
  value: number,
|}>;

export type Cell = StringCell | NumberCell;
export type Line = $ReadOnlyArray<Cell>;

export type FixedWidthTextFile = $ReadOnly<{|
  header: Line | null,
  lines: $ReadOnlyArray<Line>,
  trailer: Line | null,
|}>;

export type ParseOptions = $ReadOnly<{|
  lineLimit?: number,
|}>;

export const DEFAULT_CHUNK_SIZE = 512; // bytes

export const stringColumnCellValue = (
  column: StringColumn,
  value: string,
): string =>
  (column.valueTransformers || []).reduce(
    (acc, transformer) =>
      typeof transformer === 'function'
        ? transformer(acc)
        : value.replace(regexParser(transformer.match), transformer.replace),
    value,
  );

export const parsedCell = (
  column: Column,
  line: string,
  columns: $ReadOnlyArray<Column>,
): Cell => {
  const columnIdx = columns.indexOf(column);
  invariant(
    columnIdx > -1,
    `Column must exist in column list: ${JSON.stringify(column)}`,
  );
  const offset = sum(columns.slice(0, columnIdx).map((col) => col.width));

  const value = line.substring(offset, offset + column.width).trim();

  switch (column.type) {
    case 'String':
      return {
        type: 'String',
        value: stringColumnCellValue(column, value),
      };
    case 'Number':
      return {
        type: 'Number',
        value: Number(value),
      };
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (column.type: empty);
      throw new Error(`Unhandled column type: ${column.type}`);
  }
};

export const parsedLine = (
  line: string,
  columns: $ReadOnlyArray<Column>,
): $ReadOnlyArray<Cell> =>
  columns.reduce(
    (acc, column) => [...acc, parsedCell(column, line, columns)],
    [],
  );

export const parsed = (
  file: Blob,
  specification: FileSpecification,
  options?: ParseOptions = { ...null },
): Promise<FixedWidthTextFile> =>
  new Promise((resolve, reject) => {
    asText(file, specification.encoding)
      .then((text) => {
        const lines = text.split(
          specification.lineDelimiter || DEFAULT_LINE_END,
        );
        const limitedLines = options.lineLimit
          ? lines.slice(
              0,
              options.lineLimit +
                (specification.header ? 1 : 0) +
                (specification.trailer ? 1 : 0),
            )
          : lines;

        const header = specification.header
          ? parsedLine(limitedLines[0], specification.header)
          : null;

        const trailer = specification.trailer
          ? parsedLine(lines[lines.length - 2], specification.trailer)
          : null;

        resolve({
          header,
          trailer,
          lines: limitedLines
            .slice(
              specification.header ? 1 : 0,
              specification.trailer ? -2 : undefined,
            )
            .map((line) => parsedLine(line, specification.columns)),
        });
      })
      .catch(reject);
  });

export const cell =
  (file: FixedWidthTextFile): ((row: number, column: number) => Cell) =>
  (row: number, column: number) =>
    file.lines[row][column];
